"use client";
import * as React from 'react';
import { Button } from '@/components/ui/button';
import { useRouter } from 'next/navigation';
import { getAllPreceptors, type BackendPreceptor } from '@/services/preceptor-service';
import { getAllImgPreceptors } from '@/services/img-preceptor-service';

interface HeroDateRangeSearchProps {
  className?: string;
  /** 'img' routes to and loads from the IMG search; defaults to NP. */
  track?: 'np' | 'img';
}

export const HeroDateRangeSearch: React.FC<HeroDateRangeSearchProps> = ({ className, track = 'np' }) => {
  const router = useRouter();
  const searchBasePath = track === 'img' ? '/preceptor/img/search' : '/preceptor/np/search';
  const isImg = track === 'img';
  const [specialtyId, setSpecialtyId] = React.useState('');
  const [stateCode, setStateCode] = React.useState('');
  const [city, setCity] = React.useState('');
  const [allPreceptors, setAllPreceptors] = React.useState<BackendPreceptor[]>([]);
  const [isLoadingPreceptors, setIsLoadingPreceptors] = React.useState(false);
  const [isStateDropdownOpen, setIsStateDropdownOpen] = React.useState(false);
  const [isSpecialtyDropdownOpen, setIsSpecialtyDropdownOpen] = React.useState(false);
  const stateDropdownRef = React.useRef<HTMLDivElement>(null);
  const specialtyDropdownRef = React.useRef<HTMLDivElement>(null);

  // Close dropdowns when clicking outside
  React.useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (stateDropdownRef.current && !stateDropdownRef.current.contains(event.target as Node)) {
        setIsStateDropdownOpen(false);
      }
      if (specialtyDropdownRef.current && !specialtyDropdownRef.current.contains(event.target as Node)) {
        setIsSpecialtyDropdownOpen(false);
      }
    }
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const STATE_LABELS: Record<string, string> = {
    AL: 'Alabama',
    AK: 'Alaska',
    AZ: 'Arizona',
    AR: 'Arkansas',
    CA: 'California',
    CO: 'Colorado',
    CT: 'Connecticut',
    DE: 'Delaware',
    FL: 'Florida',
    GA: 'Georgia',
    HI: 'Hawaii',
    ID: 'Idaho',
    IL: 'Illinois',
    IN: 'Indiana',
    IA: 'Iowa',
    KS: 'Kansas',
    KY: 'Kentucky',
    LA: 'Louisiana',
    ME: 'Maine',
    MD: 'Maryland',
    MA: 'Massachusetts',
    MI: 'Michigan',
    MN: 'Minnesota',
    MS: 'Mississippi',
    MO: 'Missouri',
    MT: 'Montana',
    NE: 'Nebraska',
    NV: 'Nevada',
    NH: 'New Hampshire',
    NJ: 'New Jersey',
    NM: 'New Mexico',
    NY: 'New York',
    NC: 'North Carolina',
    ND: 'North Dakota',
    OH: 'Ohio',
    OK: 'Oklahoma',
    OR: 'Oregon',
    PA: 'Pennsylvania',
    RI: 'Rhode Island',
    SC: 'South Carolina',
    SD: 'South Dakota',
    TN: 'Tennessee',
    TX: 'Texas',
    UT: 'Utah',
    VT: 'Vermont',
    VA: 'Virginia',
    WA: 'Washington',
    WV: 'West Virginia',
    WI: 'Wisconsin',
    WY: 'Wyoming',
    DC: 'District of Columbia',
  };

  const formatStateLabel = React.useCallback((stateCode: string) => {
    const trimmed = stateCode.trim();
    const upper = trimmed.toUpperCase();
    const fullName = STATE_LABELS[upper];
    return fullName || trimmed;
  }, [STATE_LABELS]);

  // Fetch all preceptors once on mount
  React.useEffect(() => {
    let cancelled = false;
    setIsLoadingPreceptors(true);
    
    (track === 'img' ? getAllImgPreceptors() : getAllPreceptors())
      .then(preceptors => {
        if (cancelled) return;
        setAllPreceptors(preceptors);
      })
      .catch(err => {
        if (!cancelled) {
          console.error('Failed to load preceptors', err);
          setAllPreceptors([]);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoadingPreceptors(false);
      });

    return () => {
      cancelled = true;
    };
  }, []);

  // Extract available specialties with IDs from preceptors data
  const availableSpecialties = React.useMemo(() => {
    const specialtyMap = new Map<number, string>();
    const nameFallback = new Map<string, number>();
    allPreceptors.forEach((p) => {
      if (p.specialties?.length) {
        p.specialties.forEach((s) => {
          if (s.id && s.name?.trim()) {
            specialtyMap.set(Number(s.id), s.name.trim());
          }
        });
      }
      const rawSpecialtyName = typeof p.specialty === 'string' ? p.specialty : p.specialty?.name;
      if (rawSpecialtyName?.trim()) {
        nameFallback.set(rawSpecialtyName.trim().toLowerCase(), 0);
      }
    });

    let nextId = 100000;
    nameFallback.forEach((_, name) => {
      if (![...specialtyMap.values()].some(v => v.toLowerCase() === name)) {
        specialtyMap.set(nextId++, name.replace(/\b\w/g, c => c.toUpperCase()));
      }
    });

    return Array.from(specialtyMap.entries())
      .map(([id, name]) => ({ id, name }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [allPreceptors]);

  const getPreceptorState = React.useCallback((p: BackendPreceptor) => {
    if (typeof (p as any).state === 'string') return (p as any).state;
    if (p.site?.state) return p.site.state;
    const firstSite = p.sites && p.sites.length ? p.sites[0] : null;
    return firstSite?.state || '';
  }, []);

  const getPreceptorCity = React.useCallback((p: BackendPreceptor) => {
    if (typeof (p as any).city === 'string') return (p as any).city;
    if (p.site?.city) return p.site.city;
    const firstSite = p.sites && p.sites.length ? p.sites[0] : null;
    return firstSite?.city || '';
  }, []);

  const selectedSpecialtyName = React.useMemo(() => {
    if (!specialtyId) return null;
    const targetId = Number(specialtyId);
    if (Number.isNaN(targetId)) return null;
    const match = availableSpecialties.find(spec => spec.id === targetId);
    return match?.name ?? null;
  }, [specialtyId, availableSpecialties]);

  // Extract available states (filtered by specialty ID if selected)
  const availableStates = React.useMemo(() => {
    let filtered = allPreceptors;
    
    if (specialtyId) {
      const id = Number(specialtyId);
      filtered = filtered.filter((p: any) =>
        p.specialty_ids?.includes(id) ||
        p.specialties?.some((s: any) => Number(s.id) === id) ||
        (selectedSpecialtyName
          ? (p.specialty?.name || '').toLowerCase().trim() === selectedSpecialtyName.toLowerCase().trim() ||
            p.specialties?.some((s: any) => (s.name || '').toLowerCase().trim() === selectedSpecialtyName.toLowerCase().trim())
          : false)
      );
    }
    
    const states = filtered
      .map((p: BackendPreceptor) => getPreceptorState(p))
      .filter((state: any): state is string => Boolean(state?.trim()));
    return Array.from(new Set(states)).sort((a, b) => a.localeCompare(b));
  }, [allPreceptors, specialtyId]);

  // Extract available cities (filtered by specialty ID and state)
  const availableCities = React.useMemo(() => {
    if (!stateCode) return [];
    
    let filtered = allPreceptors;
    
    if (specialtyId) {
      const id = Number(specialtyId);
      filtered = filtered.filter((p: any) =>
        p.specialty_ids?.includes(id) ||
        p.specialties?.some((s: any) => Number(s.id) === id) ||
        (selectedSpecialtyName
          ? (p.specialty?.name || '').toLowerCase().trim() === selectedSpecialtyName.toLowerCase().trim() ||
            p.specialties?.some((s: any) => (s.name || '').toLowerCase().trim() === selectedSpecialtyName.toLowerCase().trim())
          : false)
      );
    }
    
    const cities = filtered
      .filter((p: BackendPreceptor) => getPreceptorState(p).toUpperCase().trim() === stateCode.toUpperCase().trim())
      .map((p: BackendPreceptor) => getPreceptorCity(p))
      .filter((city: any): city is string => Boolean(city?.trim()));
    return Array.from(new Set(cities)).sort((a, b) => a.localeCompare(b));
  }, [allPreceptors, specialtyId, stateCode]);

  // Clear dependent filters when parent filter changes
  React.useEffect(() => {
    if (!stateCode) {
      setCity('');
    }
  }, [stateCode]);

  function buildQuery() {
    const params = new URLSearchParams();
    if (specialtyId) params.set('specialty_id', specialtyId);
    if (stateCode) {
      params.set('state', stateCode);
      // Use location param for current search experience filtering.
      params.set('location', stateCode);
    }
    if (city) params.set('city', city);
    return params.toString();
  }

  function submit(e: React.FormEvent) {
    e.preventDefault();
    const qs = buildQuery();
    router.push(`${searchBasePath}${qs ? `?${qs}` : ''}`);
  }

  const footerHint = specialtyId
    ? 'If your specialty/state isn’t listed, we may not have a matching preceptor there yet.'
    : 'Select a specialty to see only states with matching preceptors.';

  return (
    <div className={"relative w-full bg-white rounded-2xl border border-gray-200 shadow-sm z-10 " + (className || '')}>
      <form
        onSubmit={submit}
        className={isImg ? 'px-6 py-5' : 'p-6'}
        aria-label="Rotation search form"
      >
        <div className={isImg ? 'space-y-3' : 'space-y-5'}>
          {/* Header */}
          <div className="text-center md:text-left pb-1">
            <h3 className="text-base font-bold text-gray-900 mb-0.5">Find Your Rotation</h3>
            <p className="text-xs text-gray-500">Search by specialty, state, and city</p>
          </div>

          {/* Fields */}
          <div className={isImg ? 'flex flex-col md:flex-row md:items-end gap-4' : 'space-y-5'}>
            <div className={"space-y-1.5" + (isImg ? ' md:flex-1' : '')}>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                Specialty
              </label>
              <div className="relative" ref={specialtyDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsSpecialtyDropdownOpen(!isSpecialtyDropdownOpen)}
                  className="w-full h-10 rounded-lg border-2 border-gray-200 bg-white px-3 text-sm font-medium text-gray-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all cursor-pointer hover:border-gray-300 text-left flex items-center justify-between"
                  disabled={isLoadingPreceptors && !availableSpecialties.length}
                >
                  <span className={specialtyId ? 'text-gray-900' : 'text-gray-500'}>
                    {specialtyId ? availableSpecialties.find(s => String(s.id) === specialtyId)?.name || 'All Specialties' : 'All Specialties'}
                  </span>
                  <svg 
                    className={`w-4 h-4 text-gray-500 transition-transform ${isSpecialtyDropdownOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m6 9 6 6 6-6" />
                  </svg>
                </button>
                {isSpecialtyDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                    <div
                      className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 text-gray-500"
                      onClick={() => { setSpecialtyId(''); setIsSpecialtyDropdownOpen(false); }}
                    >
                      All Specialties
                    </div>
                    {availableSpecialties.map(spec => (
                      <div
                        key={spec.id}
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${specialtyId === String(spec.id) ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-900'}`}
                        onClick={() => { setSpecialtyId(String(spec.id)); setIsSpecialtyDropdownOpen(false); }}
                      >
                        {spec.name}
                      </div>
                    ))}
                    {isLoadingPreceptors && !availableSpecialties.length && (
                      <div className="px-3 py-2 text-sm text-gray-400">Loading specialties...</div>
                    )}
                  </div>
                )}
              </div>
            </div>

            <div className={"space-y-1.5" + (isImg ? ' md:flex-1' : '')}>
              <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider">
                State
              </label>
              <div className="relative" ref={stateDropdownRef}>
                <button
                  type="button"
                  onClick={() => setIsStateDropdownOpen(!isStateDropdownOpen)}
                  className="w-full h-10 rounded-lg border-2 border-gray-200 bg-white px-3 text-sm font-medium text-gray-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all cursor-pointer hover:border-gray-300 text-left flex items-center justify-between"
                >
                  <span className={stateCode ? 'text-gray-900' : 'text-gray-500'}>
                    {stateCode ? formatStateLabel(stateCode) : 'All States'}
                  </span>
                  <svg 
                    className={`w-4 h-4 text-gray-500 transition-transform ${isStateDropdownOpen ? 'rotate-180' : ''}`} 
                    fill="none" 
                    stroke="currentColor" 
                    viewBox="0 0 24 24"
                  >
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m6 9 6 6 6-6" />
                  </svg>
                </button>
                {isStateDropdownOpen && (
                  <div className="absolute z-50 w-full mt-1 bg-white border-2 border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                    <div
                      className="px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 text-gray-500"
                      onClick={() => { setStateCode(''); setIsStateDropdownOpen(false); }}
                    >
                      All States
                    </div>
                    {availableStates.map(code => (
                      <div
                        key={code}
                        className={`px-3 py-2 text-sm cursor-pointer hover:bg-blue-50 ${stateCode === code ? 'bg-blue-100 text-blue-700 font-medium' : 'text-gray-900'}`}
                        onClick={() => { setStateCode(code); setIsStateDropdownOpen(false); }}
                      >
                        {formatStateLabel(code)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {stateCode && availableCities.length > 0 && (
              <div className={"space-y-1.5" + (isImg ? ' md:flex-1' : '')}>
                <label className="block text-xs font-semibold text-gray-700 uppercase tracking-wider" htmlFor="city">
                  City (Optional)
                </label>
                <div className="relative">
                  <select
                    id="city"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                    className="w-full h-10 rounded-lg border-2 border-gray-200 bg-white px-3 text-sm font-medium text-gray-900 focus:outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 transition-all appearance-none cursor-pointer hover:border-gray-300"
                    style={{
                      backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%236b7280' stroke-width='2' stroke-linecap='round' stroke-linejoin='round'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")`,
                      backgroundRepeat: 'no-repeat',
                      backgroundPosition: 'right 0.75rem center',
                      paddingRight: '2.5rem'
                    }}
                    disabled={isLoadingPreceptors}
                  >
                    <option value="">All Cities</option>
                    {availableCities.map(cityName => (
                      <option key={cityName} value={cityName}>{cityName}</option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            <Button
              type="submit"
              size="default"
              className={
                isImg
                  ? 'w-full md:w-auto h-10 px-8 bg-[var(--brand-primary)] hover:bg-[var(--brand-primary-hover)] text-white font-semibold rounded-lg shadow-sm transition-all duration-200 whitespace-nowrap'
                  : 'w-full h-10 bg-gradient-to-r from-blue-600 to-cyan-600 hover:from-blue-700 hover:to-cyan-700 text-white font-semibold rounded-lg shadow-lg shadow-blue-200 hover:shadow-xl hover:shadow-blue-300 transition-all duration-200'
              }
            >
              {isImg ? (
                <span className="inline-flex items-center gap-2">
                  Search Preceptors
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="m21 21-4.3-4.3M11 19a8 8 0 1 0 0-16 8 8 0 0 0 0 16Z" />
                  </svg>
                </span>
              ) : 'Search Preceptors →'}
            </Button>
          </div>

          {/* Footer hint */}
          <p className={"text-center text-gray-400 uppercase " + (isImg ? 'text-[7px] tracking-normal pt-0' : 'text-[9px] tracking-wider pt-0.5')}>
            {footerHint}
          </p>
        </div>
      </form>
    </div>
  );
};
